import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  SESSION_KEYS,
  setSessionHospital,
  setSessionClinic,
} from '@/constants';
import { featureService, DEFAULT_CLINIC_FEATURES } from '@/services/featureService';

const DEFAULT_CLINICS = [
  {
    id: '101',
    name: 'General Medicine OPD',
    code: 'GEN',
    department: 'Internal Medicine',
    floor: 'Ground Floor, Wing A',
    operatingHours: '08:00 AM - 04:00 PM',
  },
  {
    id: '102',
    name: 'Cardiology Speciality Clinic',
    code: 'CARD',
    department: 'Cardiology',
    floor: '1st Floor, Block B',
    operatingHours: '09:00 AM - 05:00 PM',
  },
  {
    id: '103',
    name: 'Pediatrics & Child Care OPD',
    code: 'PED',
    department: 'Pediatrics',
    floor: 'Ground Floor, Wing C',
    operatingHours: '09:00 AM - 05:00 PM',
  },
];

const ClinicContext = createContext(null);

export const useClinic = () => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error('useClinic must be used within a ClinicProvider');
  }
  return context;
};

export const ClinicProvider = ({ children }) => {
  // Read verified hospital from sessionStorage first, then localStorage
  const [verifiedHospital, setVerifiedHospitalState] = useState(() => {
    if (typeof window !== 'undefined') {
      const sessionHospId = sessionStorage.getItem(SESSION_KEYS.HOSPITAL_ID);
      const sessionHospName = sessionStorage.getItem(SESSION_KEYS.HOSPITAL_NAME);
      if (sessionHospId && sessionHospId !== 'hosp-1') {
        return { id: sessionHospId, name: sessionHospName || `Hospital #${sessionHospId}` };
      } else if (sessionHospId === 'hosp-1') {
        sessionStorage.removeItem(SESSION_KEYS.HOSPITAL_ID);
      }

      const saved = localStorage.getItem('pulseflow_pms_hospital');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed?.id && parsed.id !== 'hosp-1') {
            setSessionHospital(parsed.id, parsed.name);
            return parsed;
          } else if (parsed?.id === 'hosp-1') {
            localStorage.removeItem('pulseflow_pms_hospital');
          }
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  });

  const hospitalId = verifiedHospital?.id || (typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEYS.HOSPITAL_ID) : null);
  const hospitalName = verifiedHospital?.name || (typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEYS.HOSPITAL_NAME) : '') || '';

  const [availableClinics, setAvailableClinics] = useState(DEFAULT_CLINICS);

  // Initialize with sessionStorage clinic, then localStorage
  const [activeClinicId, setActiveClinicIdState] = useState(() => {
    if (typeof window !== 'undefined') {
      const sessionClinicId = sessionStorage.getItem(SESSION_KEYS.CLINIC_ID);
      if (sessionClinicId && sessionClinicId !== 'c-opd') {
        return sessionClinicId;
      } else if (sessionClinicId === 'c-opd') {
        sessionStorage.removeItem(SESSION_KEYS.CLINIC_ID);
      }

      const saved = localStorage.getItem('pms_active_clinic');
      if (saved && saved !== 'c-opd') {
        setSessionClinic(saved);
        return saved;
      } else if (saved === 'c-opd') {
        localStorage.removeItem('pms_active_clinic');
      }
    }
    return null;
  });

  const activeClinic = useMemo(() => {
    if (!activeClinicId) return availableClinics[0] || null;
    return availableClinics.find((c) => c.id === activeClinicId) || availableClinics[0] || null;
  }, [availableClinics, activeClinicId]);

  useEffect(() => {
    if (activeClinicId) {
      setSessionClinic(activeClinicId);
      localStorage.setItem('pms_active_clinic', activeClinicId);
    }
  }, [activeClinicId]);

  const setActiveClinicId = useCallback((newId) => {
    if (!newId) return;
    setSessionClinic(newId);
    setActiveClinicIdState((prev) => (prev === newId ? prev : newId));
  }, []);

  const setVerifiedHospital = useCallback((hospitalData) => {
    if (hospitalData && hospitalData.id) {
      setSessionHospital(hospitalData.id, hospitalData.name);
      localStorage.setItem('pulseflow_pms_hospital', JSON.stringify(hospitalData));
      setVerifiedHospitalState((prev) => {
        if (prev && prev.id === hospitalData.id && prev.name === hospitalData.name) {
          return prev;
        }
        return hospitalData;
      });
    }
  }, []);

  const setHospitalId = useCallback(
    (newHospId) => {
      if (!newHospId) return;
      setSessionHospital(newHospId);
      setVerifiedHospitalState((prev) => {
        if (prev && prev.id === newHospId) return prev;
        const updated = prev ? { ...prev, id: newHospId } : { id: newHospId, name: `Hospital #${newHospId}` };
        localStorage.setItem('pulseflow_pms_hospital', JSON.stringify(updated));
        return updated;
      });
    },
    []
  );

  const clearVerifiedHospital = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(SESSION_KEYS.HOSPITAL_ID);
      sessionStorage.removeItem(SESSION_KEYS.HOSPITAL_NAME);
      sessionStorage.removeItem(SESSION_KEYS.CLINIC_ID);
      sessionStorage.removeItem(SESSION_KEYS.CLINIC_NAME);
      localStorage.removeItem('pulseflow_pms_hospital');
    }
    setVerifiedHospitalState(null);
  }, []);

  // Helper to build scoped URLs with strict validation
  const buildUrl = useCallback(
    (subpath = '') => {
      const cleanSubpath = subpath.replace(/^\//, '');
      const hosp = hospitalId || (typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEYS.HOSPITAL_ID) : null);
      const cln = activeClinicId || (typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_KEYS.CLINIC_ID) : null);

      if (!hosp) {
        throw new Error('Hospital ID is mandatory to build URLs. No active hospital context.');
      }

      return cleanSubpath ? `/hospital/${hosp}/clinic/${cln || 'all'}/${cleanSubpath}` : `/hospital/${hosp}/clinic/${cln || 'all'}`;
    },
    [hospitalId, activeClinicId]
  );

  const switchClinic = useCallback(
    (clinicId, navigate, currentSubpath = 'dashboard') => {
      const found = availableClinics.find((c) => c.id === clinicId);
      if (found) {
        setActiveClinicId(clinicId);
        if (navigate && hospitalId) {
          navigate(`/hospital/${hospitalId}/clinic/${clinicId}/${currentSubpath.replace(/^\//, '')}`);
        }
      }
    },
    [availableClinics, hospitalId, setActiveClinicId]
  );

  const [clinicFeatures, setClinicFeatures] = useState(DEFAULT_CLINIC_FEATURES);

  useEffect(() => {
    let isMounted = true;
    const loadFeatures = async () => {
      if (activeClinicId && hospitalId) {
        try {
          const fetched = await featureService.getFeatures(activeClinicId, hospitalId);
          if (isMounted && fetched) {
            setClinicFeatures(fetched);
          }
        } catch {
          // ignore error, defaults already set
        }
      } else {
        setClinicFeatures(DEFAULT_CLINIC_FEATURES);
      }
    };

    loadFeatures();
    return () => {
      isMounted = false;
    };
  }, [activeClinicId, hospitalId]);

  const isFeatureEnabled = useCallback(
    (featureKey) => {
      if (!featureKey) return true;
      const key = String(featureKey).toLowerCase().trim();

      switch (key) {
        case 'dashboard':
          return clinicFeatures?.dashboardEnabled !== false;
        case 'patients':
          return clinicFeatures?.patientsEnabled !== false;
        case 'appointments':
          return clinicFeatures?.appointmentsEnabled !== false;
        case 'consultations':
          return clinicFeatures?.consultationsEnabled !== false;
        case 'doctors':
          return clinicFeatures?.doctorsEnabled !== false;
        case 'reports':
          return clinicFeatures?.reportsEnabled === true;
        case 'payments':
        case 'billing':
          return clinicFeatures?.paymentsEnabled !== false;
        case 'settings':
          return true;
        default:
          return true;
      }
    },
    [clinicFeatures]
  );

  const updateClinicFeatures = useCallback(
    async (newSettings) => {
      if (!activeClinicId) return;
      const merged = { ...clinicFeatures, ...newSettings };
      setClinicFeatures(merged);
      const res = await featureService.updateFeatures(activeClinicId, merged, hospitalId);
      if (res) {
        setClinicFeatures(res);
      }
      return res;
    },
    [activeClinicId, hospitalId, clinicFeatures]
  );

  const value = useMemo(
    () => ({
      hospitalId,
      hospitalName,
      verifiedHospital,
      setVerifiedHospital,
      clearVerifiedHospital,
      setHospitalId,
      activeClinicId,
      setActiveClinicId,
      activeClinic,
      availableClinics,
      setAvailableClinics,
      switchClinic,
      buildUrl,
      clinicFeatures,
      isFeatureEnabled,
      updateClinicFeatures,
    }),
    [
      hospitalId,
      hospitalName,
      verifiedHospital,
      setVerifiedHospital,
      clearVerifiedHospital,
      setHospitalId,
      activeClinicId,
      setActiveClinicId,
      activeClinic,
      availableClinics,
      switchClinic,
      buildUrl,
      clinicFeatures,
      isFeatureEnabled,
      updateClinicFeatures,
    ]
  );

  return <ClinicContext.Provider value={value}>{children}</ClinicContext.Provider>;
};

export default ClinicContext;
